<script setup lang="ts">
/**
 * BaseSidePanel — выезжающая сбоку панель (slide-over) для overlay-контента.
 *
 * Teleport в #app + затемнение (AppBackdrop) + панель, прибитая к правому (по
 * умолчанию) или левому краю. Закрытие по клику на backdrop и по Esc. Используется
 * для карточки клиента в мессенджере на узких экранах (<1280), где постоянной
 * третьей колонки нет, для мобильной шторки меню (`side="left"`) и для плашки локации
 * на мобильном (`side="bottom"`: на всю ширину, до 85% высоты, выезд снизу).
 */
import { useScrollLock } from '@vueuse/core';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import { useFocusTrap } from '@/shared/lib/composables';
import { IconCross } from '@/shared/ui/icons';
import { AppBackdrop } from '@/shared/ui/overlay';

interface Props {
  open: boolean;
  width?: string;
  zIndex?: number;
  /** Край экрана, от которого выезжает панель. */
  side?: 'bottom' | 'left' | 'right';
  /** Имя диалога для скринридера: заголовок в шапке — разметка, а не подпись роли. */
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  width: '320px',
  zIndex: 1045,
  side: 'right',
  label: undefined,
});

const emit = defineEmits<{
  close: [];
}>();

const backdropZIndex = computed(() => props.zIndex - 1);

const isBottom = computed(() => props.side === 'bottom');
const offscreenClass = computed(
  () => ({ bottom: 'translate-y-full', left: '-translate-x-full', right: 'translate-x-full' })[props.side],
);
const onscreenClass = computed(() => (isBottom.value ? 'translate-y-0' : 'translate-x-0'));
const placementClass = computed(
  () =>
    ({
      bottom: 'inset-x-0 bottom-0 max-h-[85dvh] w-full rounded-t-2xl',
      left: 'top-0 left-0 h-full pt-[env(safe-area-inset-top,0px)]',
      right: 'top-0 right-0 h-full pt-[env(safe-area-inset-top,0px)]',
    })[props.side],
);

// Ссылка на панель — для focus-trap (фокус внутрь при открытии, цикл Tab).
const panelRef = ref<HTMLElement | null>(null);

// Паритет модальности с BaseModal/BaseBottomSheet: фон не скроллится под панелью.
const bodyScrollLocked = useScrollLock(typeof document === 'undefined' ? null : document.body);

// Focus-trap: фокус внутри панели, возврат на триггер при закрытии.
useFocusTrap(
  () => panelRef.value,
  () => props.open,
);

const handleClose = (): void => {
  emit('close');
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    handleClose();
  }
};

// `immediate`: панель открывается и по прямой ссылке, где `open=true` уже на
// первом рендере. Без этого Esc и блокировка скролла включались бы только со
// второго открытия.
watch(
  () => props.open,
  (isOpen) => {
    bodyScrollLocked.value = isOpen;

    if (typeof document === 'undefined') {
      return;
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('keydown', handleKeydown);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  bodyScrollLocked.value = false;

  if (typeof document !== 'undefined') {
    document.removeEventListener('keydown', handleKeydown);
  }
});
</script>

<template>
  <Teleport to="#app">
    <AppBackdrop
      :show="open"
      data-modal-ignore
      :z-index="backdropZIndex"
      @click="handleClose"
    />

    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      leave-active-class="transition-transform duration-200 ease-in"
      :enter-from-class="offscreenClass"
      :enter-to-class="onscreenClass"
      :leave-from-class="onscreenClass"
      :leave-to-class="offscreenClass"
    >
      <aside
        v-if="open"
        ref="panelRef"
        data-modal-ignore
        data-pull-to-refresh-ignore
        class="fixed flex max-w-full flex-col bg-(--base-container-bg) shadow-xl"
        :class="placementClass"
        :style="{ width: isBottom ? undefined : width, zIndex }"
        role="dialog"
        aria-modal="true"
        :aria-label="label"
        tabindex="-1"
      >
        <div class="flex shrink-0 items-center gap-2 border-b border-(--base-popup-border-color) px-4 py-3">
          <div class="min-w-0 flex-1">
            <slot name="header" />
          </div>
          <button
            type="button"
            class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-(--base-popup-secondary-color) transition-colors hover:bg-(--base-secondary-color) hover:text-(--base-text-color)"
            aria-label="Закрыть"
            @click="handleClose"
          >
            <IconCross
              :width="20"
              :height="20"
            />
          </button>
        </div>

        <div class="scrollbar min-h-0 flex-1 overflow-y-auto">
          <slot />
        </div>

        <div
          v-if="$slots.footer"
          class="shrink-0 border-t border-(--base-popup-border-color) px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6"
        >
          <slot name="footer" />
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>
